"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { trackEvent, currentPageInfo } from "../lib/engageXClient";

/**
 * Site-wide tracking from the Website Event Tracking Plan, without wiring
 * every component by hand:
 *
 *   data-track="enquire_click" data-track-section="hero"
 *       -> a click sends enquire_click { section: "hero" } (any data-track-* becomes a parameter)
 *   data-track-view="certificate_view" data-track-certificate-type="khda"
 *       -> sent once per page when at least half of the element is on screen
 *   data-track-links="faq_link_click" data-track-question="..."
 *       -> any link clicked inside it sends faq_link_click { question, destination }
 *
 * Plus, with no markup at all: phone (tel:) and email (mailto:) links, social
 * icons, scroll depth (25/50/75/90%), engaged time (30 s / 60 s / 3 min of the
 * tab being visible) and the page-type views (blog, testimonial, corporate,
 * course lists). Everything goes through trackEvent(): Rybbit, EngageX, GTM.
 */

const SOCIAL = { "facebook.com": "facebook", "instagram.com": "instagram", "linkedin.com": "linkedin", "twitter.com": "twitter", "x.com": "twitter", "youtube.com": "youtube", "tiktok.com": "tiktok", "snapchat.com": "snapchat" };
const SCROLL_MARKS = [25, 50, 75, 90];
// First path segments that are site sections, not course pages.
const NOT_COURSE = new Set(["blog", "testimonial", "corporate-trainings", "browsecourses", "category_type", "about", "contact", "pay-now", "lpTalks", "auth", "profile", "bookings", "api"]);

// Plain links to these pages count as the plan's nav events wherever they sit
// (footer, in-page); the header's own links carry data-track instead.
const NAV_TARGETS = { "corporate-trainings": "nav_corporate_training_click", testimonial: "nav_testimonials_click", browsecourses: "nav_browse_courses_click" };

/** "/p/cma-course" or "/ar/cma-course" -> "cma-course"; null for any other page. */
const courseSlugOf = (url) => {
  if (url.origin !== window.location.origin) return null;
  const parts = url.pathname.split("/").filter(Boolean);
  if (parts[0] === "ar" || parts[0] === "en") parts.shift();
  if (parts.length === 2 && parts[0] === "p") return parts[1];
  if (parts.length === 1 && !NOT_COURSE.has(parts[0])) return parts[0];
  return null;
};
const TIME_MARKS = [30, 60, 180];

/** data-track-course-name="CMA" -> { course_name: "CMA" } */
const paramsOf = (el, skip) => {
  const out = {};
  for (const [key, value] of Object.entries(el.dataset || {})) {
    if (!key.startsWith("track") || key === skip || key === "track" || key === "trackLinks" || value === "") continue;
    const name = key.slice(5).replace(/^[A-Z]/, (c) => c.toLowerCase()).replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    if (name && name !== "view") out[name] = value;
  }
  return out;
};

const socialOf = (href) => {
  try {
    const host = new URL(href).hostname.replace(/^www\./, "").replace(/^[a-z]{2}\./, "");
    return Object.entries(SOCIAL).find(([d]) => host === d || host.endsWith(`.${d}`))?.[1] || null;
  } catch {
    return null;
  }
};

export default function TrackingAuto() {
  const pathname = usePathname();

  // Clicks: marked elements, phone/email links, social icons.
  useEffect(() => {
    const onClick = (event) => {
      const marked = event.target?.closest?.("[data-track]");
      if (marked) {
        trackEvent(marked.dataset.track, paramsOf(marked));
        return;
      }
      const link = event.target?.closest?.("a[href]");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      const area = link.closest("[data-track-links]");
      if (area && !href.startsWith("tel:") && !href.startsWith("mailto:")) {
        let destination = href;
        let courseSlug = null;
        try {
          const url = new URL(link.href);
          destination = url.origin === window.location.origin ? url.pathname : url.hostname + url.pathname;
          courseSlug = courseSlugOf(url);
        } catch {}
        let name = area.dataset.trackLinks;
        // A blog link to anything other than a course is a plain blog_link_click.
        if (name === "blog_to_course_click" && !courseSlug) name = "blog_link_click";
        trackEvent(name, { ...paramsOf(area), destination, ...(courseSlug ? { course_name: courseSlug } : {}) });
        return;
      }
      let nav = null;
      try {
        const url = new URL(link.href);
        const parts = url.pathname.split("/").filter(Boolean);
        if (parts[0] === "ar" || parts[0] === "en") parts.shift();
        if (url.origin === window.location.origin && parts.length === 1) nav = NAV_TARGETS[parts[0]] || null;
      } catch {}
      if (nav) {
        trackEvent(nav, { nav_location: link.closest(".footer") ? "footer" : "in_page" });
        return;
      }
      if (href.startsWith("tel:")) trackEvent("call_click", {});
      else if (href.startsWith("mailto:")) trackEvent("email_click", {});
      else {
        const platform = socialOf(link.href);
        if (platform) trackEvent("social_click", { platform });
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  // Per page: views, scroll depth, engaged time, seen sections.
  useEffect(() => {
    if (!pathname) return undefined;
    const page = currentPageInfo();
    const timers = [];

    // Page-type views (course pages send view_course themselves, with course details).
    const titleOf = () => (document.title || "").split("|")[0].trim();
    timers.push(setTimeout(() => {
      if (page.page_type === "blog" && pathname.split("/").filter(Boolean).length > 1) trackEvent("blog_view", { article_title: titleOf() });
      if (page.page_type === "testimonial") trackEvent("testimonial_view", {});
      if (page.page_type === "corporate") trackEvent("view_corporate", {});
      if (page.page_type === "listing" || page.page_type === "category") trackEvent("view_item_list", { list_name: page.page_type, category: pathname.split("/").filter(Boolean).pop() });
    }, 800));

    // Scroll depth, each mark once per page.
    const scrolled = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = Math.round((window.scrollY / max) * 100);
      for (const mark of SCROLL_MARKS) {
        if (pct >= mark && !scrolled.has(mark)) { scrolled.add(mark); trackEvent("scroll_depth", { percent: mark }); }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Engaged time: seconds the tab is actually visible on this page.
    let visibleMs = 0;
    let last = document.visibilityState === "visible" ? Date.now() : null;
    const sent = new Set();
    const tick = () => {
      if (last !== null) { visibleMs += Date.now() - last; last = Date.now(); }
      for (const mark of TIME_MARKS) {
        if (visibleMs >= mark * 1000 && !sent.has(mark)) { sent.add(mark); trackEvent("engaged_time", { seconds: mark }); }
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") last = Date.now();
      else { tick(); last = null; }
    };
    document.addEventListener("visibilitychange", onVisibility);
    const interval = setInterval(tick, 5000);

    // Sections seen: data-track-view, once per page each.
    let observer = null;
    const seen = new WeakSet();
    if (typeof IntersectionObserver === "function") {
      observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || seen.has(entry.target)) continue;
          seen.add(entry.target);
          trackEvent(entry.target.dataset.trackView, paramsOf(entry.target, "trackView"));
          observer.unobserve(entry.target);
        }
      }, { threshold: 0.5 });
      // Sections render after data loads, so look again for a few seconds.
      const scan = () => document.querySelectorAll("[data-track-view]").forEach((el) => { if (!seen.has(el)) observer.observe(el); });
      scan();
      [1500, 4000, 8000].forEach((ms) => timers.push(setTimeout(scan, ms)));
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
      timers.forEach(clearTimeout);
      if (observer) observer.disconnect();
    };
  }, [pathname]);

  return null;
}
